import clsx from 'clsx';
import { Check, Copy, Download, ExternalLink, MonitorSmartphone, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDays, formatNumber, relativeTime } from '../lib/format';
import { Badge, Button, Card, CardHeader, StatusDot, useToast, type BadgeTone } from './ui';
import type { AgentInfo, AgentStatus } from './ShiftControl';

/** One of my computers, as `GET /attendance/agent/devices` lists it — never its activity. */
export interface MyComputer {
  id: string;
  name: string;
  osName: string;
  osVersion: string | null;
  agentVersion: string | null;
  status: 'ACTIVE' | 'DISABLED' | 'REVOKED';
  online: boolean;
  asleep: boolean;
  pairedAt: string;
  lastHeartbeatAt: string | null;
  statusReason: string | null;
}

interface PairingCode {
  code: string;
  expiresAt: string;
  minutes: number;
}

export const AGENT_STATUS_TONE: Record<AgentStatus, BadgeTone> = {
  CONNECTED: 'success',
  OFFLINE: 'warning',
  NOT_PAIRED: 'neutral',
  DISABLED: 'error',
};

/**
 * The address the agent reaches the API at: this console's own `/api/v1`, which
 * Nginx sends to the API — so whatever address the admin is opened at, the agent
 * it connects talks to the same server.
 */
export function agentServerUrl(): string {
  return `${window.location.origin}/api/v1`;
}

/** The link the agent registers on the computer: one click connects it with the code. */
export function pairingLink(code: string): string {
  const params = new URLSearchParams({ code: code.replace(/-/g, ''), server: agentServerUrl() });
  return `viganium-agent://pair?${params.toString()}`;
}

/** Whether this browser runs on a Mac — its download comes first then. */
function onMac(): boolean {
  const hinted = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
  return /mac/i.test(hinted ?? navigator.platform ?? '') || /Macintosh|Mac OS X/.test(navigator.userAgent);
}

interface AgentDownload {
  label: TranslationKey;
  url: string;
}

/** The installers there are, the one for the computer in use first. */
function agentDownloads(agent: Pick<AgentInfo, 'downloadUrl' | 'downloadUrlMac'>): AgentDownload[] {
  const windows: AgentDownload | null = agent.downloadUrl ? { label: 'agent.downloadWindows', url: agent.downloadUrl } : null;
  const mac: AgentDownload | null = agent.downloadUrlMac ? { label: 'agent.downloadMac', url: agent.downloadUrlMac } : null;
  return (onMac() ? [mac, windows] : [windows, mac]).filter((d): d is AgentDownload => d !== null);
}

/**
 * The Desktop Agent, on the employee's own Attendance page (the agent CR's §46.1):
 * where theirs stands, the rule it carries, connecting a computer, and their
 * computers. Connecting is a code the employee makes here, signed in — so the
 * agent always belongs to whoever is using this console (§9).
 *
 * A notification about one of their computers (disabled, enabled, removed) opens
 * the page with their computers in view and that one marked: `focus` is its id.
 */
export function AgentPanel({ agent, focus }: { agent: AgentInfo; focus?: string | null }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pairing, setPairing] = useState<PairingCode | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const now = useNow(pairing ? 1000 : 30_000);
  const intoView = useCallback((el: HTMLDivElement | null) => el?.scrollIntoView({ block: 'center' }), []);

  const computers = useQuery({
    queryKey: ['attendance', 'agent', 'devices'],
    queryFn: () => api.get<MyComputer[]>('/attendance/agent/devices'),
    // While a code is out, look for the computer it connects every few seconds.
    refetchInterval: pairing ? 3_000 : 30_000,
  });

  const knownIds = useMemo(() => new Set((computers.data ?? []).map((c) => c.id)), [computers.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const [idsAtCode, setIdsAtCode] = useState<Set<string> | null>(null);

  const makeCode = useMutation({
    mutationFn: () => api.post<PairingCode>('/attendance/agent/pairing-code', {}),
    onSuccess: (code) => {
      setPairing(code);
      setCopied(false);
      setIdsAtCode(new Set(knownIds));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.post<MyComputer[]>(`/attendance/agent/devices/${id}/remove`, {}),
    onSuccess: (list) => {
      queryClient.setQueryData(['attendance', 'agent', 'devices'], list);
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'me'] });
      setConfirmRemove(null);
      toast.success(t('agent.removed'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // The computer the code connected: one we did not have, or one connected again since.
  const connected = useMemo(() => {
    if (!pairing || !idsAtCode) return null;
    const madeAt = new Date(pairing.expiresAt).getTime() - pairing.minutes * 60_000;
    return (
      (computers.data ?? []).find((c) => !idsAtCode.has(c.id) || new Date(c.pairedAt).getTime() >= madeAt - 1000) ?? null
    );
  }, [computers.data, pairing, idsAtCode]);

  useEffect(() => {
    if (!connected) return;
    toast.success(t('agent.paired', { name: connected.name }));
    setPairing(null);
    void queryClient.invalidateQueries({ queryKey: ['attendance', 'me'] });
  }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

  const expired = pairing ? new Date(pairing.expiresAt).getTime() <= now : false;
  const left = pairing ? Math.max(0, Math.round((new Date(pairing.expiresAt).getTime() - now) / 1000)) : 0;
  const list = (computers.data ?? []).filter((c) => c.status !== 'REVOKED');

  const copy = async () => {
    if (!pairing) return;
    try {
      await navigator.clipboard.writeText(pairing.code);
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard: the code is on screen to type.
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <MonitorSmartphone className="h-4 w-4 text-[var(--text-tertiary)]" aria-hidden />
            {t('agent.title')}
            <Badge tone={AGENT_STATUS_TONE[agent.status]} dot={agent.status === 'CONNECTED'}>
              {t(`agent.status.${agent.status}` as TranslationKey)}
            </Badge>
            {agent.required && <Badge tone="brand">{t('agent.requiredBadge')}</Badge>}
          </span>
        }
        subtitle={t('agent.subtitle')}
      />

      <p className="max-w-3xl text-body-sm text-[var(--text-primary)]">
        {agent.status === 'CONNECTED' && agent.device
          ? t('agent.connectedOn', { name: agent.device.name, when: relativeTime(agent.device.lastHeartbeatAt, locale) })
          : agent.status === 'OFFLINE' && agent.device
            ? t('agent.offlineOn', {
                name: agent.device.name,
                when: agent.device.lastHeartbeatAt ? relativeTime(agent.device.lastHeartbeatAt, locale) : t('agent.never'),
              })
            : agent.status === 'DISABLED' && agent.device
              ? t('agent.disabledOn', { name: agent.device.name })
              : t('agent.notPairedBody')}
      </p>
      <p className="mt-1 max-w-3xl text-body-sm text-muted">{agent.required ? t('agent.requiredNote') : t('agent.notRequiredNote')}</p>
      {agent.idleEnabled && (
        <p className="mt-1 max-w-3xl text-body-sm text-muted">
          {agent.penaltyEnabled
            ? t('agent.policy', {
                idle: formatNumber(agent.idleThresholdMinutes, locale),
                penalty: formatNumber(agent.penaltyThresholdMinutes, locale),
                days: formatDays(agent.penaltyDays, locale),
              })
            : t('agent.policyNoPenalty', { idle: formatNumber(agent.idleThresholdMinutes, locale) })}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {agentDownloads(agent).map((d) => (
          <a key={d.label} href={d.url} target="_blank" rel="noreferrer">
            <Button icon={<Download className="h-4 w-4" />}>{t(d.label)}</Button>
          </a>
        ))}
        <Button
          variant={agent.status === 'CONNECTED' ? 'secondary' : 'primary'}
          icon={<MonitorSmartphone className="h-4 w-4" />}
          loading={makeCode.isPending}
          onClick={() => makeCode.mutate()}
        >
          {list.length > 0 ? t('agent.connectAnother') : t('agent.connect')}
        </Button>
      </div>

      {pairing && (
        <div className="mt-4 rounded-lg border border-primary/35 bg-primary/8 p-4" aria-live="polite">
          <p className="text-overline uppercase text-[var(--text-tertiary)]">{t('agent.codeTitle')}</p>
          {expired ? (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('agent.codeExpired')}</p>
              <Button size="sm" icon={<RefreshCw className="h-4 w-4" />} loading={makeCode.isPending} onClick={() => makeCode.mutate()}>
                {t('agent.newCode')}
              </Button>
            </div>
          ) : (
            <>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="font-mono text-h2 tracking-[0.2em] text-[var(--text-primary)]" dir="ltr">
                  {pairing.code}
                </span>
                <Button size="sm" variant="ghost" icon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} onClick={copy}>
                  {copied ? t('agent.copied') : t('agent.copyCode')}
                </Button>
                <a href={pairingLink(pairing.code)}>
                  <Button size="sm" icon={<ExternalLink className="h-4 w-4 flip-rtl" />}>
                    {t('agent.openAgent')}
                  </Button>
                </a>
              </div>
              <p className="mt-2 max-w-2xl text-caption text-muted">
                {t('agent.codeHint', { minutes: formatNumber(pairing.minutes, locale) })}
              </p>
              <p className="mt-2 flex items-center gap-2 text-caption text-muted">
                <StatusDot tone="idle" pulse />
                {t('agent.waiting')}{' '}
                <span className="tabular" dir="ltr">
                  {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
                </span>
              </p>
            </>
          )}
        </div>
      )}

      <div ref={focus ? intoView : undefined} className="mt-5 border-t hairline pt-4">
        <p className="mb-2 text-body-sm font-medium text-[var(--text-secondary)]">{t('agent.myComputers')}</p>
        {list.length === 0 ? (
          <p className="text-body-sm text-muted">{t('agent.noComputers')}</p>
        ) : (
          <ul className="divide-y divide-[var(--line)]">
            {list.map((c) => (
              <li
                key={c.id}
                className={clsx(
                  'flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5',
                  c.id === focus && '-mx-2 rounded-md bg-primary/8 px-2 ring-1 ring-inset ring-primary/30',
                )}
                aria-current={c.id === focus ? 'true' : undefined}
              >
                <StatusDot tone={c.status === 'DISABLED' ? 'alert' : c.online ? 'online' : 'offline'} pulse={c.online} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body-sm text-[var(--text-primary)]">{c.name}</p>
                  <p className="text-caption text-muted">
                    {[c.osName, c.osVersion, c.agentVersion && t('agent.version', { version: c.agentVersion })].filter(Boolean).join(' · ')}
                  </p>
                  {c.status === 'DISABLED' && (
                    <p className="text-caption text-error-fg dark:text-error-dark">
                      {t('agent.disabledBy')}
                      {c.statusReason ? ` — ${c.statusReason}` : ''}
                    </p>
                  )}
                </div>
                <span className={clsx('text-caption', c.online ? 'text-success-fg dark:text-success' : 'text-muted')}>
                  {c.status === 'DISABLED'
                    ? t('devices.status.DISABLED')
                    : c.online
                      ? t('devices.online')
                      : c.asleep
                        ? t('devices.asleep')
                        : t('devices.lastReport', {
                            when: c.lastHeartbeatAt ? relativeTime(c.lastHeartbeatAt, locale) : t('agent.never'),
                          })}
                </span>
                {confirmRemove === c.id ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-caption text-[var(--text-secondary)]">{t('agent.removeConfirm', { name: c.name })}</span>
                    <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate(c.id)}>
                      {t('agent.remove')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(null)}>
                      {t('common.cancel')}
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmRemove(c.id)}>
                    {t('agent.remove')}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-4 flex max-w-3xl items-start gap-2 text-caption text-muted">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success-fg dark:text-success" aria-hidden />
        {t('agent.privacy')}
      </p>
    </Card>
  );
}

/** The current time, moving on every `everyMs`. */
function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}
